import React from 'react';
import { Button, Box, Typography, Chip, Skeleton } from '@mui/joy';
import { Eye as ViewIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import type { StormAlert } from '../../types/stormtracker';
import { EVENT_TYPE_LABELS } from '../../types/stormtracker';
import { SeverityBadge } from './SeverityBadge';
import { OpportunityScoreBadge } from './OpportunityScoreBadge';

interface AlertsTableProps {
  alerts: StormAlert[];
  isLoading?: boolean;
  showTerritory?: boolean;
  emptyMessage?: string;
}

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (diffDays === 1) {
    return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const getMagnitudeDisplay = (alert: StormAlert): string => alert.magnitude_display || '—';

const getEventIcon = (eventType: string): string => {
  switch (eventType) {
    case 'hail':
      return '🌨️';
    case 'wind':
      return '💨';
    case 'tornado':
      return '🌪️';
    default:
      return '⛈️';
  }
};

export const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  isLoading = false,
  showTerritory = true,
  emptyMessage = 'No alerts found',
}) => {
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (alerts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const columns: ResponsiveColumn<StormAlert>[] = [
    { key: 'event', header: 'Event', mobileVariant: 'header', render: alert => <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><span aria-hidden="true">{getEventIcon(alert.event_type)}</span><Chip size="sm" variant="outlined">{EVENT_TYPE_LABELS[alert.event_type]}</Chip></Box> },
    { key: 'date', header: 'Date / time', render: alert => formatDateTime(alert.event_timestamp) },
    { key: 'location', header: 'Location', render: alert => <Box>{[alert.city, alert.state].filter(Boolean).join(', ') || 'Unknown'}<Typography level="body-xs">{alert.zip_code}</Typography></Box> },
    { key: 'severity', header: 'Severity', render: alert => <SeverityBadge severity={alert.severity} /> },
    { key: 'magnitude', header: 'Magnitude', render: getMagnitudeDisplay },
    { key: 'score', header: 'Score', render: alert => <OpportunityScoreBadge score={alert.opportunity_score} /> },
    ...(showTerritory ? [{ key: 'territory', header: 'Watchlist', render: (alert: StormAlert) => alert.territory_name || '—' }] : []),
  ];
  return <ResponsiveTable columns={columns} rows={alerts} getRowKey={alert => alert.id}
    rowActions={alert => <Button component={Link} to={`/advanced/storm-tracker/alerts/${alert.id}`} size="sm" variant="plain" aria-label={`Open ${EVENT_TYPE_LABELS[alert.event_type]} alert in ${alert.city || alert.zip_code || 'unknown location'}`} startDecorator={<ViewIcon />}>Open</Button>} />;
};
export default AlertsTable;
